import * as Icons from "@nova/component-icons"
import { UserLocation } from "@nova/schemas"
import { useTimeRemaining } from "@nova/toolkit-pure-hooks"
import { ReactNode } from "react"
import { setUserProfileModalContext } from "features/content-overlay/slice"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectWorkspaceMembersById } from "features/entity-caching/members/UserManagementSlice"
import { useSharingMemberCount } from "features/overview/sharing-location/sharingMembers"
import { TimeRemainingPie } from "features/overview/sharing-location/TimeRemainingPie"
import {
    MembersSortBy,
    MembersSortDirection,
    selectMembersSort,
    setMembersSortBy,
    setMembersSortDirection,
} from "features/overview/slice"
import { useOrgUserLocation } from "features/user-locations/org-user-location-store"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { showGlobalModal } from "runlevel/actions"
import { SearchResultComponent } from "../registry"
import { LiveBadge } from "./LiveBadge"
import { useProjectSearchResultClickHandler } from "./ProjectSearchResult"
import { Dropdown } from "@nova/component-dropdown"
import { getBatteryIcon } from "plugins/UserLocationShare/ui/getBatteryIcon"

type Props = SearchResultComponent<{ userId: string }>

const useWorkspaceMember = (userId: string) => {
    const membersById = useAppSelector(selectWorkspaceMembersById)
    return membersById[userId]
}

const useSharingMemberClickHandler = ({
    userId,
    location,
    onAfterSelect,
}: {
    userId: string
    location: UserLocation | undefined
    onAfterSelect: () => void
}) => {
    const dispatch = useAppDispatch()
    const projectClickHandler = useProjectSearchResultClickHandler({
        projectId: location?.projectId ?? "",
        onAfterSelect,
    })

    return () => {
        if (location && projectClickHandler) {
            projectClickHandler()
            return
        }
        dispatch(setUserProfileModalContext({ userId }))
        dispatch(showGlobalModal())
        onAfterSelect()
    }
}

export const WorkspaceMemberSearchResult = ({ userId, onAfterSelect }: Props) => {
    const member = useWorkspaceMember(userId)
    const location = useOrgUserLocation(userId)
    const handleClick = useSharingMemberClickHandler({ userId, location, onAfterSelect })

    if (!member) return null

    return (
        <div
            onClick={handleClick}
            className="flex items-center gap-2 w-full px-3 py-2 hover:bg-white-100 cursor-pointer"
        >
            <div className="w-8 h-8 shrink-0 overflow-hidden rounded-md">
                <ProfilePhoto userId={userId} fullName={member.fullName} />
            </div>
            <div className="flex flex-col min-w-0 shrink grow">
                <div className="flex items-center gap-2 min-w-0 truncate">{member.fullName}</div>
                <span className="text-sm text-gray-500 truncate">{member.email}</span>
            </div>
            {!!location && <LiveBadge className="shrink-0" />}
        </div>
    )
}

const sortByOptions: { value: MembersSortBy; label: string }[] = [
    { value: "alphabetical", label: "Alphabetical" },
    { value: "timeRemaining", label: "Time remaining" },
]

const orderLabelsBySortBy: Record<MembersSortBy, Record<MembersSortDirection, string>> = {
    alphabetical: { ascending: "Ascending", descending: "Descending" },
    timeRemaining: { ascending: "Earliest", descending: "Latest" },
}

const SortMenuItem = ({
    selected,
    onClick,
    children,
}: {
    selected: boolean
    onClick: () => void
    children: ReactNode
}) => (
    <Dropdown.ButtonItem clickBehaviour="keep-open" onClick={onClick} className="!px-0">
        <div className="flex items-center h-7 w-full">
            <div className="w-10 shrink-0 flex justify-center items-center">
                {selected && <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />}
            </div>
            <small className="text-white">{children}</small>
        </div>
    </Dropdown.ButtonItem>
)

export const SharingMembersFullPanelHeader = () => {
    const dispatch = useAppDispatch()
    const count = useSharingMemberCount()
    const { by, direction } = useAppSelector(selectMembersSort)
    const orderLabels = orderLabelsBySortBy[by]

    return (
        <div className="pt-2 px-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
                <div className="text-lg text-white telegraf">Sharing Location</div>
                <div className="px-2 flex justify-center items-center rounded-full h-5 min-w-[30px] shrink-0 font-mono text-xs bg-white-100 text-gray-300">
                    {count}
                </div>
            </div>
            <Dropdown stopPropagation>
                <Dropdown.Trigger className="h-full shrink-0 flex items-center group gap-1">
                    <Icons.SmallIcon
                        Icon={Icons.Sort}
                        className="text-gray-400 group-hover:text-gray-200"
                    />
                    <div className="text-gray-400 group-hover:text-gray-200">Sort</div>
                </Dropdown.Trigger>
                <Dropdown.Menu position="bottom-right">
                    <div className="py-1 bg-gray-700 flex flex-col gap-1 w-[150px]">
                        <div className="flex flex-col">
                            <small className="text-gray-300 pl-10 py-1 xs">Sort by</small>
                            {sortByOptions.map((option) => (
                                <SortMenuItem
                                    key={option.value}
                                    selected={by === option.value}
                                    onClick={() => dispatch(setMembersSortBy(option.value))}
                                >
                                    {option.label}
                                </SortMenuItem>
                            ))}
                        </div>
                        <div className="h-px w-full bg-white-200" />
                        <div className="flex flex-col">
                            <small className="text-gray-300 pl-10 py-1 xs">Order</small>
                            <SortMenuItem
                                selected={direction === "ascending"}
                                onClick={() => dispatch(setMembersSortDirection("ascending"))}
                            >
                                {orderLabels.ascending}
                            </SortMenuItem>
                            <SortMenuItem
                                selected={direction === "descending"}
                                onClick={() => dispatch(setMembersSortDirection("descending"))}
                            >
                                {orderLabels.descending}
                            </SortMenuItem>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

export const SharingMemberFullPanelResult = ({ userId, onAfterSelect }: Props) => {
    const member = useWorkspaceMember(userId)
    const location = useOrgUserLocation(userId)
    const project = useAppSelector(selectFireById(location?.projectId))
    const timeRemaining = useTimeRemaining({ timestamp: location?.expiresAt })
    const handleClick = useSharingMemberClickHandler({ userId, location, onAfterSelect })

    if (!member) return null
    if (!location) {
        return <WorkspaceMemberSearchResult userId={userId} onAfterSelect={onAfterSelect} />
    }

    return (
        <div
            className="w-full p-3 flex gap-3 items-center cursor-pointer overflow-hidden hover:bg-white-100"
            onClick={handleClick}
        >
            <div className="shrink-0 w-8 h-8 rounded-md overflow-hidden">
                <ProfilePhoto userId={userId} fullName={member.fullName} />
            </div>
            <div className="flex flex-col min-w-0 grow shrink">
                <div className="flex items-center gap-1 min-w-0">
                    <div className="min-w-0 truncate text-white">{member.fullName}</div>
                    <Icons.SmallIcon
                        Icon={getBatteryIcon({ percentage: location.batteryPercentage })}
                        className="text-white-300"
                    />
                </div>
                <small className="text-gray-300 truncate">
                    {project?.name ?? "Unknown project"}
                </small>
            </div>
            <div className="flex items-center gap-1.5">
                <small className="text-white-400">{timeRemaining}</small>
                <TimeRemainingPie expiresAt={location.expiresAt} />
            </div>
        </div>
    )
}

export const SharingMembersFullPanelEmpty = ({ query }: { query: string }) => (
    <div className="px-4 py-3 flex flex-col gap-1">
        <div className="text-white telegraf">No results</div>
        <small className="text-gray-400">
            {query
                ? "No people sharing their location matches your search"
                : "No people sharing their location"}
        </small>
    </div>
)
